// 자체 헤드리스 트리 — OmTree 의 라이브 백엔드(Phase D2, AntTree 대체 완료).
// OmwTreeProps 계약을 antd 무관하게 구현: 제어형/비제어형 selectedKeys/expandedKeys/checkedKeys +
// 트라이스테이트 체크박스 + @tanstack/react-virtual 가상화(virtual+height) + scrollTo ref.
// ⚠ DOM 구조·클래스 taxonomy 를 antd Tree 와 미러링(`.om-tree-*`) → §3.5 SCSS 전환이
//    `.om-tree-` → `.om-tree-` 기계적 치환으로 성립하도록 설계.
import {
  cloneElement,
  isValidElement,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { OmwTreeNode, OmwTreeProps, OmwTreeRef } from '../../api/OmwTree'
import { cx } from '../cx'
import { useRovingKeydown } from '../a11y/useRovingKeydown'
import {
  buildTreeIndex,
  computeCheckState,
  deriveCheckedKeys,
  flattenVisible,
  toggleCheck,
  type FlatTreeRow,
} from './treeCore'
import '../../styles/omwTree.css'

// 가상 스크롤(virtual)에서만 쓰는 고정 행높이(react-virtual estimateSize·translateY 계산 기준).
// 비가상 모드에서는 treenode 에 inline height 를 주지 않고 소비처 SCSS(.om-tree-treenode
// { height: … })가 행높이를 제어한다 — inline 을 강제하면 mailbox(40)/MailboxSelector(34)/
// SharedContacts(40) 등 소비처 SCSS 높이가 무조건 override 돼 행이 28px 로 뭉친다.
const ROW_HEIGHT = 28

// antd Tree 파리티: switcher 아이콘의 `.om-tree-switcher-icon` 클래스는 antd 처럼 아이콘 엘리먼트
// (SVG/img) 에 "직접" 부여한다. span 으로 감싸면 vertical-align 이 래퍼에만 걸리고 내부 SVG 는
// baseline 정렬로 남아 아이콘이 어긋난다(소비처 SCSS 의 .om-tree-switcher-icon 정렬 규칙 대상이
// SVG 자체여야 함). 커스텀 아이콘은 cloneElement 로 className 을 병합하고, 기본 텍스트 화살표만
// 클래스를 실을 대상이 없으므로 span 으로 감싼다.
function renderSwitcherIcon(custom: ReactNode, expanded: boolean): ReactNode {
  if (isValidElement(custom)) {
    const el = custom as ReactElement<{ className?: string }>
    return cloneElement(el, {
      className: cx('om-tree-switcher-icon', el.props.className),
    })
  }
  return <span className="om-tree-switcher-icon">{custom ?? (expanded ? '▾' : '▸')}</span>
}
// indent 폭은 styles/omTree.css(.om-tree-indent-unit { width })가 제어한다(antd 기본 24px,
// 소비처 SCSS override 가능 — 예: mailbox 8px). 인라인 width 를 주면 소비처 SCSS 를
// 덮어써 antd 파리티가 깨지므로 부여하지 않는다.

export function HeadlessTree({
  treeData,
  selectable = true,
  multiple,
  selectedKeys,
  onSelect,
  expandedKeys,
  onExpand,
  autoExpandParent,
  defaultExpandAll,
  defaultExpandParent,
  checkable,
  checkedKeys,
  onCheck,
  switcherIcon,
  titleRender,
  showIcon,
  virtual,
  height,
  onClick,
  className,
  style,
  'data-testid': dataTestid,
  treeRef,
}: OmwTreeProps & { treeRef?: Ref<OmwTreeRef> }) {
  const index = useMemo(() => buildTreeIndex(treeData), [treeData])

  // 비제어형 확장 폴백: expandedKeys 미지정 시 내부 상태(defaultExpandAll/Parent 시드).
  const [innerExpanded, setInnerExpanded] = useState<string[]>(() => {
    if (defaultExpandAll === true) return [...index.byKey.keys()]
    if (defaultExpandParent === true || autoExpandParent === true) return [...index.parentOf.keys()]
    return []
  })
  const isExpandControlled = expandedKeys != null
  const effectiveExpanded = isExpandControlled ? expandedKeys : innerExpanded

  const expandedSet = useMemo(() => new Set(effectiveExpanded), [effectiveExpanded])
  const selectedSet = useMemo(() => new Set(selectedKeys ?? []), [selectedKeys])
  const rows = useMemo(() => flattenVisible(treeData, expandedSet), [treeData, expandedSet])

  // antd checkedKeys(부모 포함) → 리프 기준 집합으로 정규화 후 노드별 상태 파생.
  const checkState = useMemo(() => {
    if (checkable !== true) return null
    const leafSet = new Set<string>()
    for (const k of checkedKeys ?? []) {
      const leaves = index.leavesOf.get(k)
      if (leaves) for (const l of leaves) leafSet.add(l)
    }
    return { leafSet, state: computeCheckState(treeData, leafSet, index) }
  }, [checkable, checkedKeys, treeData, index])

  const scrollParentRef = useRef<HTMLDivElement>(null)
  // antd 파리티: height 가 지정되면 고정높이 스크롤 컨테이너(overflow:auto)를 만든다.
  // 윈도잉(가상 렌더)은 명시적 virtual===true 일 때만(OrgChart, POC 검증된 ROW_HEIGHT 파리티).
  // mailbox 는 virtual 미지정 + height 만 주므로 스크롤은 되되 윈도잉은 하지 않는다.
  const hasHeight = typeof height === 'number'
  const isVirtual = virtual === true && hasHeight

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
    enabled: isVirtual,
  })

  useImperativeHandle(treeRef, () => ({
    scrollTo: ({ key, align }) => {
      const idx = rows.findIndex((r) => r.key === key)
      if (idx < 0) return
      if (isVirtual) {
        const vAlign = align === 'top' ? 'start' : align === 'bottom' ? 'end' : 'auto'
        virtualizer.scrollToIndex(idx, { align: vAlign })
      } else {
        scrollParentRef.current
          ?.querySelector<HTMLElement>(`[data-tree-key="${CSS.escape(key)}"]`)
          ?.scrollIntoView({ block: align === 'bottom' ? 'end' : 'start' })
      }
    },
  }))

  const handleSelect = (row: FlatTreeRow, e: React.MouseEvent): void => {
    if (row.node.disabled === true) return
    onClick?.(e.nativeEvent, row.node)
    if (selectable === false || row.node.selectable === false) return
    const wasSelected = selectedSet.has(row.key)
    let nextKeys: string[]
    if (multiple === true) {
      nextKeys = wasSelected
        ? (selectedKeys ?? []).filter((k) => k !== row.key)
        : [...(selectedKeys ?? []), row.key]
    } else {
      nextKeys = wasSelected ? [] : [row.key]
    }
    onSelect?.(nextKeys, {
      selected: !wasSelected,
      node: row.node,
      selectedNodes: nextKeys.map((k) => index.byKey.get(k)).filter(Boolean) as OmwTreeNode[],
      nativeEvent: e.nativeEvent,
    })
  }

  const handleToggleExpand = (row: FlatTreeRow, e: React.MouseEvent): void => {
    e.stopPropagation()
    const willExpand = !row.expanded
    const nextKeys = willExpand
      ? [...effectiveExpanded, row.key]
      : effectiveExpanded.filter((k) => k !== row.key)
    if (!isExpandControlled) setInnerExpanded(nextKeys)
    onExpand?.(nextKeys, { expanded: willExpand, node: row.node, nativeEvent: e.nativeEvent })
  }

  const handleCheck = (row: FlatTreeRow, e: React.MouseEvent): void => {
    e.stopPropagation()
    if (checkState == null || row.node.disabled === true) return
    const cur = checkState.state.get(row.key)
    const nextChecked = !(cur?.checked ?? false)
    const nextLeafSet = toggleCheck(treeData, checkState.leafSet, row.key, nextChecked, index)
    const derived = deriveCheckedKeys(treeData, nextLeafSet, index)
    onCheck?.(derived.checked, { checked: nextChecked, node: row.node, nativeEvent: e.nativeEvent })
  }

  // ── 키보드 네비게이션(roving tabindex) ──────────────────────────────
  // 활성 노드만 tabIndex=0. 활성화는 실제 DOM .click() 위임으로 마우스 경로와 동일 동작 보장
  // (콜백 nativeEvent:MouseEvent 계약 유지 — 키보드 이벤트를 억지로 넘기지 않음).
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const firstEnabledKey = rows.find((r) => r.node.disabled !== true)?.key ?? null
  const anchorKey = activeKey ?? firstEnabledKey
  const activeIndex = rows.findIndex((r) => r.key === activeKey)
  const rafRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    },
    [],
  )

  const queryNode = (key: string): HTMLElement | null | undefined =>
    scrollParentRef.current?.querySelector<HTMLElement>(`[data-tree-key="${CSS.escape(key)}"]`)

  const focusRowByKey = (key: string): void => {
    const el = queryNode(key)
    if (el) {
      el.focus()
      return
    }
    // 가상 스크롤: 활성 노드가 윈도우 밖이면 DOM 미존재 → scrollToIndex 후 다음 프레임에 focus.
    if (!isVirtual) return
    const idx = rows.findIndex((r) => r.key === key)
    if (idx < 0) return
    virtualizer.scrollToIndex(idx, { align: 'auto' })
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      queryNode(key)?.focus()
    })
  }

  const goToIndex = (i: number): void => {
    const r = rows[i]
    if (!r) return
    setActiveKey(r.key)
    focusRowByKey(r.key)
  }

  const clickWithin = (key: string, selector: string): void => {
    queryNode(key)?.querySelector<HTMLElement>(selector)?.click()
  }

  const parentIndexOf = (i: number): number => {
    const d = rows[i]?.depth ?? 0
    for (let j = i - 1; j >= 0; j -= 1) if (rows[j].depth < d) return j
    return -1
  }

  const lastKey = useRef('')
  const { onKeyDown: rovingKeyDown } = useRovingKeydown({
    itemCount: rows.length,
    activeIndex,
    setActiveIndex: goToIndex,
    isDisabled: (i) => rows[i]?.node.disabled === true,
    getLabel: (i) => {
      const t = rows[i]?.node.title
      return typeof t === 'string' ? t : ''
    },
    orientation: 'vertical',
    // Enter=select, Space=checkable 면 체크 토글 / 아니면 select(WAI-ARIA).
    onActivate: (i) => {
      const r = rows[i]
      if (!r || r.node.disabled === true) return
      if (lastKey.current === ' ' && checkable === true) clickWithin(r.key, '.om-tree-checkbox')
      else clickWithin(r.key, '.om-tree-node-content-wrapper')
    },
    // → 접힘이면 펼침, 펼쳐졌으면 첫 자식(다음 가시행)으로.
    onArrowRight: (i) => {
      const r = rows[i]
      if (!r) return false
      if (r.hasChildren && !r.expanded) {
        clickWithin(r.key, '.om-tree-switcher')
        return true
      }
      if (r.hasChildren && r.expanded) {
        goToIndex(i + 1)
        return true
      }
      return false
    },
    // ← 펼쳐졌으면 접힘, 아니면 부모로.
    onArrowLeft: (i) => {
      const r = rows[i]
      if (!r) return false
      if (r.hasChildren && r.expanded) {
        clickWithin(r.key, '.om-tree-switcher')
        return true
      }
      const p = parentIndexOf(i)
      if (p >= 0) {
        goToIndex(p)
        return true
      }
      return false
    },
  })

  const onTreeKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    lastKey.current = e.key
    rovingKeyDown(e)
  }

  const renderRow = (row: FlatTreeRow): React.JSX.Element => {
    const cs = checkState?.state.get(row.key)
    const isSelected = selectedSet.has(row.key)
    const disabled = row.node.disabled === true
    // antd Tree 파리티: treeData 노드의 data-*/aria-* 커스텀 속성을 treenode DOM 으로 통과시킨다
    // (mailbox 는 data-mailbox-id/data-mailbox-role/data-testid 를 노드에 심어 drag-drop·E2E 에서 조회).
    const passthroughAttrs: Record<string, unknown> = {}
    for (const attrKey in row.node) {
      if (attrKey.startsWith('data-') || attrKey.startsWith('aria-')) {
        passthroughAttrs[attrKey] = row.node[attrKey]
      }
    }
    return (
      <div
        key={row.key}
        data-tree-key={row.key}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={row.hasChildren ? row.expanded : undefined}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? undefined : anchorKey === row.key ? 0 : -1}
        onFocus={() => setActiveKey(row.key)}
        className={cx(
          'om-tree-treenode',
          row.hasChildren &&
            (row.expanded ? 'om-tree-treenode-switcher-open' : 'om-tree-treenode-switcher-close'),
          !row.hasChildren && 'om-tree-treenode-leaf',
          isSelected && 'om-tree-treenode-selected',
          disabled && 'om-tree-treenode-disabled',
          row.node.className,
        )}
        // antd Tree 파리티: treeData 노드의 style 을 treenode DOM 에 적용한다(OmwTreeNode.style 계약).
        // 소비처가 노드별 여백/가시성을 style 로 제어(SharedContacts marginLeft:-20px, OrgChart
        // 무효 사용자 display:none). virtual 높이는 뒤에 병합해 항상 우선(가상 스크롤 안전).
        style={{ ...row.node.style, ...(isVirtual ? { height: ROW_HEIGHT } : null) }}
        {...passthroughAttrs}
      >
        <span className="om-tree-indent" aria-hidden>
          {Array.from({ length: row.depth }, (_, i) => (
            <span key={i} className="om-tree-indent-unit" />
          ))}
        </span>
        <span
          className={cx(
            'om-tree-switcher',
            row.hasChildren
              ? row.expanded
                ? 'om-tree-switcher_open'
                : 'om-tree-switcher_close'
              : 'om-tree-switcher-noop',
          )}
          onClick={row.hasChildren ? (e) => handleToggleExpand(row, e) : undefined}
        >
          {row.hasChildren
            ? renderSwitcherIcon(switcherIcon?.({ expanded: row.expanded }), row.expanded)
            : null}
        </span>
        {checkable === true && (
          <span
            className={cx(
              'om-tree-checkbox',
              cs?.checked && 'om-tree-checkbox-checked',
              cs?.indeterminate && 'om-tree-checkbox-indeterminate',
              disabled && 'om-tree-checkbox-disabled',
            )}
            role="checkbox"
            aria-checked={cs?.indeterminate ? 'mixed' : (cs?.checked ?? false)}
            onClick={(e) => handleCheck(row, e)}
          >
            <span className="om-tree-checkbox-inner" />
          </span>
        )}
        <span
          className={cx(
            'om-tree-node-content-wrapper',
            row.expanded
              ? 'om-tree-node-content-wrapper-open'
              : 'om-tree-node-content-wrapper-close',
            isSelected && 'om-tree-node-selected',
          )}
          onClick={(e) => handleSelect(row, e)}
        >
          {showIcon === true && row.node.icon != null && (
            <span className="om-tree-iconEle">{row.node.icon}</span>
          )}
          <span className="om-tree-title">
            {titleRender ? titleRender(row.node) : row.node.title}
          </span>
        </span>
      </div>
    )
  }

  return (
    <div className={cx('om-tree', className)} style={style} data-testid={dataTestid}>
      <div className="om-tree-list">
        <div className="om-tree-list-holder">
          <div
            ref={scrollParentRef}
            className="om-tree-list-holder-scroll"
            style={{ overflow: hasHeight ? 'auto' : undefined, height }}
          >
            <div
              className="om-tree-list-holder-inner"
              role="tree"
              onKeyDown={onTreeKeyDown}
              style={
                isVirtual ? { height: virtualizer.getTotalSize(), position: 'relative' } : undefined
              }
            >
              {isVirtual
                ? virtualizer.getVirtualItems().map((vi) => (
                    <div
                      key={rows[vi.index].key}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${vi.start}px)`,
                      }}
                    >
                      {renderRow(rows[vi.index])}
                    </div>
                  ))
                : rows.map((row) => renderRow(row))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
